import React, { useState, useEffect } from 'react';
import {
    addProduct,
    getAllProducts,
    deleteProduct,
    updateProduct,
    getCategories,
    addCategory,
    deleteCategory,
    Product,
    Category,
    Inquiry,
    getInquiries,
    deleteInquiry,
    updateInquiry
} from '../utils/productManager';
import { Plus, Lock, LayoutDashboard, Package, LogOut, Trash2, Edit2, Layers, Tag as TagIcon, UploadCloud, Loader, Inbox, Menu, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AdminPage: React.FC = () => {
    // Auth State
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    // UI State
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [view, setView] = useState<string>('products');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [filterCategory, setFilterCategory] = useState<string>('All');

    // Data State
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);

    // Form State (Product)
    const initialProductState: Partial<Product> = {
        name: '',
        category: '',
        price: 'Custom Order',
        description: '',
        image: '',
        images: [''],
        code: '',
        material: '',
        dimensions: ''
    };
    const [productForm, setProductForm] = useState<Partial<Product>>(initialProductState);
    const [editingProductId, setEditingProductId] = useState<number | null>(null);

    // Form State (Category)
    const [newCategoryName, setNewCategoryName] = useState('');

    // Form State (Inquiry)
    const [editingInquiryId, setEditingInquiryId] = useState<number | null>(null);
    const [inquiryForm, setInquiryForm] = useState<Partial<Inquiry>>({});

    // --- Helpers ---
    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const refreshData = async () => {
        setLoading(true);
        try {
            const [pData, cData, iData] = await Promise.all([getAllProducts(), getCategories(), getInquiries()]);
            setProducts(pData);
            setCategories(cData);
            setInquiries(iData);
        } catch (e) {
            console.error("Failed to fetch data", e);
            showToast("Failed to load data", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            refreshData();
        }
    }, [isAuthenticated, view]);

    // --- Handlers ---

    const handleLogin = (e: React.FormEvent) => {
        e.preventDefault();
        if (password === "shb*T*GUYUG^2w2w878&") {
            setIsAuthenticated(true);
            setError('');
        } else {
            setError('Invalid Password');
        }
    };

    const handleProductSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingProductId) {
                await updateProduct(editingProductId, productForm);
                showToast('Product Updated Successfully');
            } else {
                await addProduct(productForm as Omit<Product, 'id'>);
                showToast('Product Added Successfully');
            }
            setProductForm(initialProductState);
            setEditingProductId(null);
            setView('products');
            refreshData();
        } catch (err) {
            showToast('Operation failed', 'error');
        }
    };

    const handleDeleteProduct = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            await deleteProduct(id);
            refreshData();
        }
    };

    const handleEditProduct = (product: Product) => {
        const images = product.images && product.images.length > 0
            ? product.images
            : (product.image ? [product.image] : ['']);

        setProductForm({ ...product, images });
        setEditingProductId(product.id);
        setView('products-edit');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setProductForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCategoryName.trim()) return;
        await addCategory(newCategoryName);
        setNewCategoryName('');
        refreshData();
    };

    const handleDeleteCategory = async (id: number) => {
        if (window.confirm('Delete this category?')) {
            await deleteCategory(id);
            refreshData();
        }
    };

    const handleDeleteInquiry = async (id: number) => {
        if (window.confirm('Delete this inquiry?')) {
            await deleteInquiry(id);
            refreshData();
        }
    };

    const handleEditInquiry = (inquiry: Inquiry) => {
        setInquiryForm(inquiry);
        setEditingInquiryId(inquiry.id);
        setView('inquiries-edit');
    };

    const handleInquiryUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (editingInquiryId) {
            const success = await updateInquiry(editingInquiryId, inquiryForm);
            if (success) {
                showToast('Inquiry Updated Successfully');
                setEditingInquiryId(null);
                setInquiryForm({});
                setView('inquiries');
                refreshData();
            } else {
                showToast('Failed to update inquiry', 'error');
            }
        }
    };

    // --- RENDER ---

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                <Helmet>
                    <title>Admin Login | D'mans</title>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <form onSubmit={handleLogin} className="bg-white p-8 md:p-12 w-full max-w-md space-y-6 text-center shadow-xl rounded-2xl">
                    <div className="flex justify-center mb-4">
                        <div className="p-4 bg-blue-600 rounded-full text-white shadow-lg">
                            <Lock size={24} />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">Admin Dashboard</h2>
                    <p className="text-gray-500 text-sm">Please enter your secure access key</p>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-center text-lg"
                    />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-md">
                        Login
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 font-sans flex flex-col md:flex-row">
            <Helmet>
                <title>Admin Dashboard | D'mans</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {/* Toast Notification */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white font-medium ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'} transition-all animate-fade-in`}>
                    {toast.message}
                </div>
            )}

            {/* Mobile Header */}
            <div className="md:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between sticky top-0 z-20">
                <h1 className="text-xl font-bold text-blue-600 tracking-tight">D'mans<span className="text-gray-400 font-light">Admin</span></h1>
                <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                    {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Sidebar Overlay (Mobile) */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-20 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed md:sticky top-0 h-screen w-64 bg-white border-r border-gray-200 flex flex-col z-30 transition-transform duration-300
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                <div className="p-6 border-b border-gray-100 hidden md:block">
                    <h1 className="text-2xl font-bold text-blue-600 tracking-tight">D'mans<span className="text-gray-400 font-light">Admin</span></h1>
                </div>

                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    <button
                        onClick={() => {
                            setProductForm({
                                ...initialProductState,
                                category: categories.length > 0 ? categories[0].name : '',
                            });
                            setEditingProductId(null);
                            setView('products-add');
                            setSidebarOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${view === 'products-add' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Plus size={18} />
                        Add Product
                    </button>
                    <button
                        onClick={() => { setView('products'); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${view === 'products' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <LayoutDashboard size={18} />
                        Products
                    </button>
                    <button
                        onClick={() => { setView('categories'); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${view === 'categories' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Layers size={18} />
                        Categories
                    </button>
                    <button
                        onClick={() => { setView('inquiries'); setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${view === 'inquiries' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Inbox size={18} />
                        Inquiries
                    </button>
                </nav>

                <div className="p-4 border-t border-gray-100">
                    <button
                        onClick={() => setIsAuthenticated(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                    >
                        <LogOut size={18} />
                        Logout
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-x-hidden w-full">

                {/* --- PRODUCTS LIST VIEW --- */}
                {view === 'products' && (
                    <div className="space-y-6">
                        <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">Products Inventory</h2>
                                <p className="text-gray-500 mt-1">Manage your catalog entries</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Filter by:</label>
                                <select
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white min-w-[160px]"
                                >
                                    <option value="All">All Categories</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {loading ? (
                                            <tr><td colSpan={3} className="p-8 text-center">Loading...</td></tr>
                                        ) : (filterCategory === 'All' ? products : products.filter(p => p.category === filterCategory)).length === 0 ? (
                                            <tr><td colSpan={3} className="p-8 text-center text-gray-500">No products found in this category.</td></tr>
                                        ) : (filterCategory === 'All' ? products : products.filter(p => p.category === filterCategory)).map((product) => (
                                            <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="h-12 w-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                                                            <img src={product.image} alt="" className="h-full w-full object-cover" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900">{product.name}</p>
                                                            <p className="text-xs text-gray-500">ID: {product.id}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                                                        {product.category}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                                                    <button onClick={() => handleEditProduct(product)} className="text-gray-400 hover:text-blue-600 p-2"><Edit2 size={16} /></button>
                                                    <button onClick={() => handleDeleteProduct(product.id)} className="text-gray-400 hover:text-red-600 p-2"><Trash2 size={16} /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- CATEGORIES VIEW --- */}
                {view === 'categories' && (
                    <div className="max-w-4xl mx-auto space-y-8">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">Manage Categories</h2>
                            <p className="text-gray-500 mt-1">Add or remove product categories</p>
                        </div>

                        {/* Add Category Form */}
                        <form onSubmit={handleAddCategory} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 md:items-end">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">New Category Name</label>
                                <input
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    placeholder="e.g. Outdoor Furniture"
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 h-10 w-full md:w-auto">
                                Add
                            </button>
                        </form>

                        {/* Categories List */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {categories.map((cat) => (
                                <div key={cat.id} className="bg-white p-4 rounded-lg border border-gray-200 flex justify-between items-center shadow-sm">
                                    <span className="font-medium text-gray-800">{cat.name}</span>
                                    <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- INQUIRIES VIEW --- */}
                {view === 'inquiries' && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">Customer Inquiries</h2>
                            <p className="text-gray-500 mt-1">Messages from the contact form</p>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {loading ? (
                                            <tr><td colSpan={5} className="p-8 text-center">Loading...</td></tr>
                                        ) : inquiries.length === 0 ? (
                                            <tr><td colSpan={5} className="p-8 text-center text-gray-500">No inquiries found.</td></tr>
                                        ) : inquiries.map((inquiry) => (
                                            <tr key={inquiry.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                    {new Date(inquiry.created_at).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="font-medium text-gray-900">{inquiry.name}</p>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${inquiry.status === 'new' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                        {inquiry.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    <p>{inquiry.phone}</p>
                                                    <p className="text-xs">{inquiry.email}</p>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                                                    <p className="font-medium text-gray-700">{inquiry.type} <span className="text-gray-400">({inquiry.budget})</span></p>
                                                    <p className="truncate" title={inquiry.message}>{inquiry.message}</p>
                                                </td>
                                                <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleEditInquiry(inquiry)}
                                                        className="text-gray-400 hover:text-blue-600 p-2"
                                                        title="Edit Inquiry"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteInquiry(inquiry.id)}
                                                        className="text-gray-400 hover:text-red-600 p-2"
                                                        title="Delete Inquiry"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- EDIT INQUIRY VIEW --- */}
                {view === 'inquiries-edit' && (
                    <div className="max-w-2xl mx-auto">
                        <div className="flex items-center gap-4 mb-8">
                            <button onClick={() => setView('inquiries')} className="text-sm text-gray-500 hover:text-gray-800">
                                &larr; Back to Inquiries
                            </button>
                            <h2 className="text-2xl font-bold text-gray-800">Edit Inquiry</h2>
                        </div>

                        <form onSubmit={handleInquiryUpdate} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={inquiryForm.status || 'new'}
                                        onChange={(e) => setInquiryForm({ ...inquiryForm, status: e.target.value as any })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    >
                                        <option value="new">New</option>
                                        <option value="read">Read</option>
                                        <option value="contacted">Contacted</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        value={inquiryForm.name || ''}
                                        onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        value={inquiryForm.email || ''}
                                        onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <input
                                        value={inquiryForm.phone || ''}
                                        onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Interest</label>
                                    <input
                                        value={inquiryForm.type || ''}
                                        onChange={(e) => setInquiryForm({ ...inquiryForm, type: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Budget</label>
                                    <input
                                        value={inquiryForm.budget || ''}
                                        onChange={(e) => setInquiryForm({ ...inquiryForm, budget: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div className="col-span-1 md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                    <textarea
                                        value={inquiryForm.message || ''}
                                        onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                                        rows={4}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={() => setView('inquiries')} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" className="px-6 py-2 bg-blue-600 rounded-lg text-white font-bold hover:bg-blue-700 transition-colors shadow-sm">
                                    Update Inquiry
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* --- ADD/EDIT PRODUCT VIEW --- */}
                {(view === 'products-add' || view === 'products-edit') && (
                    <div className="max-w-2xl mx-auto">
                        <div className="flex items-center gap-4 mb-8">
                            <button onClick={() => setView('products')} className="text-sm text-gray-500 hover:text-gray-800">
                                &larr; Back to List
                            </button>
                            <h2 className="text-2xl font-bold text-gray-800">
                                {view === 'products-edit' ? 'Edit Product' : 'Create New Product'}
                            </h2>
                        </div>

                        <form onSubmit={handleProductSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-8">
                            {/* Basic Info */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-1 md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                                        <input
                                            name="name" value={productForm.name} onChange={handleInputChange} required
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                        <select
                                            name="category" value={productForm.category} onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                        >
                                            <option value="" disabled>Select Category</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-1 md:col-span-2">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-gray-700">Product Images</label>
                                            <button
                                                type="button"
                                                onClick={() => document.getElementById('image-upload-input')?.click()}
                                                disabled={uploading}
                                                className="flex items-center gap-2 text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                                            >
                                                {uploading ? (
                                                    <span className="flex items-center gap-2">
                                                        <Loader size={14} className="animate-spin" /> Uploading...
                                                    </span>
                                                ) : (
                                                    <>
                                                        <UploadCloud size={14} />
                                                        Upload from Device
                                                    </>
                                                )}
                                            </button>
                                            <input
                                                id="image-upload-input"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    setUploading(true);
                                                    try {
                                                        const formData = new FormData();
                                                        formData.append('file', file);
                                                        formData.append('upload_preset', 'dman-images');
                                                        formData.append('cloud_name', 'dznpfvra9');

                                                        const res = await fetch('https://api.cloudinary.com/v1_1/dznpfvra9/image/upload', {
                                                            method: 'POST',
                                                            body: formData
                                                        });

                                                        const data = await res.json();
                                                        if (data.secure_url) {
                                                            const newImages = [...(productForm.images || [''])];
                                                            // If the last item is empty, use it, otherwise push new
                                                            const lastIdx = newImages.length - 1;
                                                            if (newImages[lastIdx] === '') {
                                                                newImages[lastIdx] = data.secure_url;
                                                            } else {
                                                                newImages.push(data.secure_url);
                                                            }

                                                            setProductForm(prev => ({
                                                                ...prev,
                                                                images: newImages,
                                                                image: newImages[0] // Ensure main image is set
                                                            }));
                                                            showToast('Image uploaded successfully');
                                                        } else {
                                                            throw new Error('Upload failed');
                                                        }
                                                    } catch (err) {
                                                        console.error(err);
                                                        showToast('Image upload failed', 'error');
                                                    } finally {
                                                        setUploading(false);
                                                        e.target.value = '';
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            {(productForm.images && productForm.images.length > 0 ? productForm.images : ['']).map((url, index) => (
                                                <div key={index} className="flex items-center gap-3">
                                                    <div className="flex-1">
                                                        <input
                                                            value={url}
                                                            onChange={(e) => {
                                                                const newImages = [...(productForm.images || [''])];
                                                                newImages[index] = e.target.value;
                                                                setProductForm(prev => ({
                                                                    ...prev,
                                                                    images: newImages,
                                                                    image: index === 0 ? e.target.value : prev.image
                                                                }));
                                                            }}
                                                            placeholder={index === 0 ? "Main Image URL" : "Additional Image URL"}
                                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                                        />
                                                    </div>

                                                    {/* Add Button (only on the last item) */}
                                                    {index === (productForm.images?.length || 1) - 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setProductForm(prev => ({ ...prev, images: [...(prev.images || ['']), ''] }))}
                                                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                                            title="Add another image"
                                                        >
                                                            <Plus size={20} />
                                                        </button>
                                                    )}

                                                    {/* Delete Button (if more than 1 row) */}
                                                    {(productForm.images?.length || 0) > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const newImages = productForm.images!.filter((_, i) => i !== index);
                                                                setProductForm(prev => ({
                                                                    ...prev,
                                                                    images: newImages,
                                                                    image: index === 0 ? newImages[0] || '' : prev.image
                                                                }));
                                                            }}
                                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Remove image"
                                                        >
                                                            <Trash2 size={20} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2">The first image will be used as the main display image.</p>

                                        {/* Image Previews */}
                                        {productForm.images && productForm.images.some(url => url.trim() !== '') && (
                                            <div className="mt-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-3">Image Preview</label>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                                    {productForm.images.map((url, index) => (
                                                        url.trim() !== '' && (
                                                            <div key={index} className="relative group aspect-square rounded-xl border-2 border-gray-200 overflow-hidden bg-gray-50 hover:border-blue-400 transition-colors">
                                                                <img
                                                                    src={url}
                                                                    alt={`Preview ${index + 1}`}
                                                                    className="w-full h-full object-contain p-2"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                                                    }}
                                                                />
                                                                <div className="hidden w-full h-full flex items-center justify-center text-xs text-gray-400">Invalid URL</div>
                                                                {index === 0 && (
                                                                    <span className="absolute top-1.5 left-1.5 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">MAIN</span>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const newImages = productForm.images!.filter((_, i) => i !== index);
                                                                        if (newImages.length === 0) newImages.push('');
                                                                        setProductForm(prev => ({
                                                                            ...prev,
                                                                            images: newImages,
                                                                            image: index === 0 ? newImages[0] || '' : prev.image
                                                                        }));
                                                                    }}
                                                                    className="absolute top-1.5 right-1.5 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                                                                    title="Remove image"
                                                                >
                                                                    <X size={12} />
                                                                </button>
                                                            </div>
                                                        )
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-1 md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <textarea
                                            name="description" value={productForm.description} onChange={handleInputChange} rows={3}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Specifications */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Specifications</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Code</label>
                                        <input
                                            name="code" value={productForm.code} onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions</label>
                                        <input
                                            name="dimensions" value={productForm.dimensions} onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                                        <input
                                            name="material" value={productForm.material} onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Price Label</label>
                                        <input
                                            name="price" value={productForm.price} onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={() => setView('products')} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" className="px-6 py-2 bg-blue-600 rounded-lg text-white font-bold hover:bg-blue-700 transition-colors shadow-sm">
                                    {view === 'products-edit' ? 'Update Product' : 'Create Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminPage;
